import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { GlobalService } from '../../services/global.service';
import { SiteService } from '../../services/site.service';
import { SectorService } from '../../services/sector.service';
import { SkillService } from '../../services/skill.service';
import { AbilityService } from '../../services/ability.service';

import { Ability, Client, CommonGlobalService, CommonUtilsService, ERROR_COLOR, GroupedOption, MultiSelect, Option, RootState, Sector, Site, Skill, UserUmanforge } from '@umanforge/common';

import { ILUOSkill } from '../../components/users/iluo_skill.component';
import { ExpirationAbility } from '../../components/users/expiration_ability.component';

import { SwitchAdminUser } from './switch_admin_user.component';
import usePrevious from 'hooks/previous.hook';
import { ClientService } from 'services/client.service';


interface ModifyUserLinksProps {
  user: UserUmanforge;
  onClientsChange: ( newSelectedClients: Client[] ) => void;
  onSitesChange: ( newSelectedSites: Site[] ) => void;
  onSectorsChange: ( newSelectedSectors: Sector[] ) => void;
  onAbilitiesChange: ( newSelectedAbilities: Ability[] ) => void;
  onSkillsChange: ( newSelectedSkills: Skill[] ) => void;
  t: ( key: string ) => string;
}


export function ModifyUserLinks( props: ModifyUserLinksProps ) {
  const {
    user,
    onClientsChange, onSitesChange, onSectorsChange,
    onAbilitiesChange, onSkillsChange, t
  } = props;

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState -----------------------------*/
  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<GroupedOption<Site>[]>( [] );
  const [ allowedSectors, setAllowedSectors ] = useState<GroupedOption<Sector>[]>( [] );
  const [ allowedAbilities, setAllowedAbilities ] = useState<Option<Ability>[]>( [] );
  const [ allowedSkills, setAllowedSkills ] = useState<Option<Skill>[]>( [] );

  const [ selectedClients, setSelectedClients ] = useState<Option<Client>[]>( [] );
  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );
  const [ selectedSectors, setSelectedSectors ] = useState<Option<Sector>[]>( [] );
  const [ selectedAbilities, setSelectedAbilities ] = useState<Option<Ability>[]>( [] );
  const [ selectedSkills, setSelectedSkills ] = useState<Option<Skill>[]>( [] );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  const previousSelectedClients = usePrevious<Option<Client>[]>( selectedClients );
  const previousSelectedSites = usePrevious<Option<Site>[]>( selectedSites );

  /*----------------------------- useEffect ----------------------------*/
  useEffect( () => {
    init();
  }, [ me ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const oldClients = ( previousSelectedClients ?? [] ).map( item => { return item.object.id + '-' + ( item.object.isAdmin ? 'yes' : 'no' ); } ).sort().join( '-' );
      const newClients = selectedClients.map( item => { return item.object.id + '-' + ( item.object.isAdmin ? 'yes' : 'no' ); } ).sort().join( '-' );
      if ( oldClients != newClients ) {
        refreshAllowedSites( selectedClients.map( client => { return client.object.id; } ) );
      }
      onClientsChange( selectedClients.map( item => { return item.object; } ) );
    }
  }, [ selectedClients ] );

  useEffect( () => {
    if ( !isFirstRender && me ) {
      const currentSelectedSites = selectedSites
        .filter( site => {
          return ( selectedClients.find( client => { return client.object.id === site.object.clientId; } ) != null );
        } );

      const updatedSelectedSites = currentSelectedSites
        .concat(
          allowedSites
            .map( site => { return site.options; } )
            .flat()
            .filter( site => {
              return (
                !currentSelectedSites.some( item => { return item.value === site.value; } ) &&
                selectedClients
                  .filter( client => { return client.object.isAdmin; } )
                  .map( client => { return client.value; } )
                  .includes( site.object.clientId )
              );
            } )
        )
        .map( site => {
          const isAdminOfClient = selectedClients.find( client => { return client.object.id === site.object.clientId; } )?.object.isAdmin === true;
          site.isDisabled = isAdminOfClient || !CommonGlobalService.isAdminOfSite( me, site.object.id );
          if ( isAdminOfClient ) {
            site.object.isAdmin = isAdminOfClient;
          }
          return site;
        } );
      setSelectedSites( updatedSelectedSites );
    }
  }, [ allowedSites ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      const oldSites = ( previousSelectedSites ?? [] ).map( item => { return item.object.id + '-' + ( item.object.isAdmin ? 'yes' : 'no' ); } ).sort().join( '-' );
      const newSites = selectedSites.map( item => { return item.object.id + '-' + ( item.object.isAdmin ? 'yes' : 'no' ); } ).sort().join( '-' );
      if ( oldSites != newSites ) {
        refreshAllowedSectors( selectedSites.map( site => { return site.object.id; } ) );
      }
      if ( selectedClients.length > 0 ) {
        const clientsAlone = selectedClients.filter( selectedClient => { return !selectedSites.find( site => { return site.object.clientId === selectedClient.object.id; } ); } );
        const updatedClients = selectedClients.map( client => {
          const isAlone = clientsAlone.find( clientAlone => { return client.value === clientAlone.value; } ) != null;
          return {
            ...client,
            color: ( isAlone ? ERROR_COLOR : undefined ),
            tooltip: ( isAlone ? t( 'translation:it_does_not_contain_any_linked_sites' )  : undefined )
          };
        } );
        setSelectedClients( updatedClients );
      }
      onSitesChange( selectedSites.map( item => { return item.object; } ) );
    }
  }, [ selectedSites ] );

  useEffect( () => {
    if ( !isFirstRender && me ) {
      const currentSelectedSectors = selectedSectors
        .filter( sector => {
          return ( selectedSites.find( client => { return client.object.id === sector.object.siteId; } ) != null );
        } );

      const updatedSelectedSectors = currentSelectedSectors
        .concat(
          allowedSectors
            .map( sector => { return sector.options; } )
            .flat()
            .filter( sector => {
              return (
                !currentSelectedSectors.some( item => { return item.value === sector.value; } ) &&
                selectedSites
                  .filter( site => { return site.object.isAdmin; } )
                  .map( site => { return site.value; } )
                  .includes( sector.object.siteId )
              );
            } )
        )
        .map( sector => {
          const isAdminOfSite = selectedSites.find( site => { return site.object.id === sector.object.siteId; } )?.object.isAdmin === true;
          sector.isDisabled = isAdminOfSite || !CommonGlobalService.isAdminOfSector( me, sector.object.id );
          if ( isAdminOfSite ) {
            sector.object.isAdmin = isAdminOfSite;
          }
          return sector;
        } );
      setSelectedSectors( updatedSelectedSectors );
    }
  }, [ allowedSectors ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      refreshAllowedAbilities( selectedSites.filter( site => { return selectedSectors.find( sector => { return sector.object.siteId === site.object.id; } ) != null; } ).map( site => { return site.object.id; } ) );
      refreshAllowedSkills( selectedSites.filter( site => { return selectedSectors.find( sector => { return sector.object.siteId === site.object.id; } ) != null; } ).map( site => { return site.object.id; } ) );
      if ( selectedSites.length > 0 ) {
        const sitesAlone = selectedSites.filter( selectedSite => { return !selectedSectors.find( sector => { return sector.object.siteId === selectedSite.object.id; } ); } );
        const updatedSites = selectedSites.map( site => {
          const isAlone = sitesAlone.find( siteAlone => { return site.value === siteAlone.value; } ) != null;
          return {
            ...site,
            color: ( isAlone ? ERROR_COLOR : undefined ),
            tooltip: ( isAlone ? t( 'translation:it_does_not_contain_any_linked_sectors' )  : undefined )
          };
        } );
        setSelectedSites( updatedSites );
      }
      onSectorsChange( selectedSectors.map( item => { return item.object; } ) );
    }
  }, [ selectedSectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sites = selectedSites.filter( site => { return selectedSectors.find( sector => { return sector.object.siteId === site.object.id; } ) != null || site.object.isAdmin === true; } );
      const newSelectedUsersAbilities: Option<Ability>[] = selectedAbilities
        .filter( ability => {
          return ( sites.find( site => { return site.object.id === ability.object.siteId; } ) != null );
        } )
        .sort( ( item1, item2 ) => {
          const date1 = item1.object.expirationDate && item1.object.expirationDate != '' ? moment( item1.object.expirationDate ) : moment();
          const date2 = item2.object.expirationDate && item2.object.expirationDate != '' ? moment( item2.object.expirationDate ) : moment();
          if ( date1.isSame( date2, 'date' ) ) {
            return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
          }
          return date1.isBefore( date2, 'date' ) ? -1 : 1;
        } );
      setSelectedAbilities( newSelectedUsersAbilities );
    }
  }, [ allowedAbilities ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      onAbilitiesChange( selectedAbilities.map( item => { return item.object; } ) );
    }
  }, [ selectedAbilities ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sites = selectedSites.filter( site => {
        const isSiteOfSelectedSectors: boolean = ( selectedSectors.find( sector => { return sector.object.siteId === site.object.id; } ) != null );
        const isSiteAdmin: boolean = ( site.object.isAdmin === true );
        return isSiteOfSelectedSectors || isSiteAdmin;
      } );
      const newSelectedUsersSkills: Option<Skill>[] = selectedSkills
        .filter( skill => {
          return ( sites.find( site => { return site.object.id === skill.object.siteId; } ) != null );
        } )
        .sort( ( item1, item2 ) => {
          const value1 = item1.object.value ?? 1;
          const value2 = item2.object.value ?? 1;
          if ( value1 === value2 ) {
            return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
          }
          return ( value1 < value2 ) ? -1 : 1;
        } );
      setSelectedSkills( newSelectedUsersSkills );
    }
  }, [ allowedSkills ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      onSkillsChange( selectedSkills.map( item => { return item.object; } ) );
    }
  }, [ selectedSkills ] );


  const init = async () => {
    if ( me ) {
      // Partie Clients
      const allowedUserClients = await ClientService.getAllowedClients( null, token, dispatch );
      setAllowedClients( CommonGlobalService.toOptions( allowedUserClients ).filter( client => { return CommonGlobalService.isAdminOfClient( me, client.object.id ); } ) );
      const defaultUserClients = CommonGlobalService.toOptions(
        CommonGlobalService.getUserClients( user )
          .filter( clientUser => { return allowedUserClients.some( client => { return clientUser.id === client.id; } ); } )
      )
        .map( client => {
          client.isDisabled = !CommonGlobalService.isAdminOfClient( me, client.object.id );
          client.canDelete = me.isSuperAdmin;
          return client;
        } );
      setSelectedClients( defaultUserClients );

      // Partie Sites
      if ( defaultUserClients.length > 0 ) {
        const allowedUserSites = await SiteService.getAllowedSitesByClientsId( defaultUserClients.map( client => { return client.object.id; } ), token, dispatch );
        const sites = CommonGlobalService.toOptions( allowedUserSites )
          .filter( site => { return CommonGlobalService.isAdminOfSite( me, site.object.id ); } );
        const groupedSites: GroupedOption<Site>[] = [];
        for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( sites, option => { return option.object.clientId; } ) ) ) {
          const optionSites = options ?? [];
          CommonUtilsService.sortByKey( optionSites, 'label', 'asc' );
          groupedSites.push( {
            label: defaultUserClients.find( client => { return client.object.id === clientId; } )?.object.name ?? t( 'common:error.missing_client' ),
            options: optionSites
          } );
        }
        setAllowedSites( groupedSites );
        const defaultUserSites = CommonGlobalService.toOptions(
          CommonGlobalService.getUserSites( user )
            .filter( siteUser => { return allowedUserSites.find( site => { return siteUser.id === site.id; } ) != null; } )
        )
          .map( site => {
            site.isDisabled = (
              !CommonGlobalService.isAdminOfSite( me, site.object.id ) ||
              defaultUserClients.find( client => { return client.object.id === site.object.clientId; } )?.object.isAdmin === true
            );
            return site;
          } );
        setSelectedSites( defaultUserSites );

        // Partie Secteurs
        if ( defaultUserSites.length > 0 ) {
          const selectedSitesId = defaultUserSites.map( site => { return site.object.id; } );
          const allowedUserSectors = await SectorService.getAllowedSectorsBySitesId( selectedSitesId, token, dispatch );
          const sectors = CommonGlobalService.toOptions( allowedUserSectors )
            .filter( sector => { return CommonGlobalService.isAdminOfSector( me, sector.object.id ); } );
          const groupedSectors: GroupedOption<Sector>[] = [];
          for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( sectors, option => { return option.object.siteId; } ) ) ) {
            const optionSectors = options ?? [];
            CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
            groupedSectors.push( {
              label: defaultUserSites.find( site => { return site.object.id === siteId; } )?.object.name ?? t( 'common:error.missing_site' ),
              options: optionSectors
            } );
          }
          setAllowedSectors( groupedSectors );
          const defaultUserSectors = CommonGlobalService.toOptions(
            CommonGlobalService.getUserSectors( user )
              .filter( sectorsUser => { return allowedUserSectors.find( sector => { return sectorsUser.id === sector.id; } ) != null; } )
          )
            .map( sector => {
              sector.isDisabled = (
                !CommonGlobalService.isAdminOfSector( me, sector.object.id ) ||
                defaultUserSites.find( site => { return site.object.id === sector.object.siteId; } )?.object.isAdmin === true
              );
              return sector;
            } );
          setSelectedSectors( defaultUserSectors );

          // Partie Habilitations
          const allowedUserAbilities = CommonGlobalService.toOptions( await AbilityService.getAllowedAbilitiesBySitesId( selectedSitesId, token, dispatch ) );
          setAllowedAbilities( allowedUserAbilities );
          setSelectedAbilities(
            CommonGlobalService.toOptions( GlobalService.getUserAbilities( user ) )
              .filter( userAbility => {
                return allowedUserAbilities.some ( ability => { return userAbility.object.id === ability.object.id; } );
              } )
              .sort( ( item1, item2 ) => {
                const date1 = item1.object.expirationDate && item1.object.expirationDate != '' ? moment( item1.object.expirationDate ) : moment();
                const date2 = item2.object.expirationDate && item2.object.expirationDate != '' ? moment( item2.object.expirationDate ) : moment();
                if ( date1.isSame( date2, 'date' ) ) {
                  return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
                }
                return date1.isBefore( date2, 'date' ) ? -1 : 1;
              } )
          );

          // Partie Compétences
          const allowedUserSkills = CommonGlobalService.toOptions( await SkillService.getAllowedSkillsBySitesId( selectedSitesId, token, dispatch ) );
          setAllowedSkills( allowedUserSkills );
          setSelectedSkills(
            CommonGlobalService.toOptions( GlobalService.getUserSkills( user ) )
              .filter( userSkill => {
                return allowedUserSkills.some ( skill => { return userSkill.object.id === skill.object.id; } );
              } )
              .sort( ( item1, item2 ) => {
                const value1 = item1.object.value ?? 1;
                const value2 = item2.object.value ?? 1;
                if ( value1 === value2 ) {
                  return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
                }
                return ( value1 < value2 ) ? -1 : 1;
              } )
          );

        } else {
          setAllowedSectors( [] );
          setSelectedSectors( [] );
          setAllowedAbilities( [] );
          setSelectedAbilities( [] );
          setAllowedSkills( [] );
          setSelectedSkills( [] );
        }
      } else {
        setAllowedSites( [] );
        setSelectedSites( [] );
        setAllowedSectors( [] );
        setSelectedSectors( [] );
        setAllowedAbilities( [] );
        setSelectedAbilities( [] );
        setAllowedSkills( [] );
        setSelectedSkills( [] );
      }
    }
    setIsFirstRender( false );
  };

  /*-------------------------- Requetes API Site -------------------------*/
  const refreshAllowedSites = async ( clientsId: string [] ) => {
    if ( clientsId.length > 0 && me ) {
      const sites = ( await SiteService.getAllowedSitesByClientsId( clientsId, token, dispatch ) )
        .filter( site => { return CommonGlobalService.isAdminOfSite( me, site.id ); } );
      if ( sites != null ) {
        const optionsSites = CommonGlobalService.toOptions( sites );
        const groupedSites: GroupedOption<Site>[] = [];
        for ( const [ clientId, options ] of Object.entries( CommonGlobalService.groupBy( optionsSites, option => { return option.object.clientId; } ) ) ) {
          const optionSites = options ?? [];
          groupedSites.push( {
            label: selectedClients.find( client => { return client.object.id === clientId; } )?.object.name ?? t( 'common:error.missing_client' ),
            options: optionSites
          } );
        }
        setAllowedSites( groupedSites );
      }
    } else {
      setAllowedSites( [] );
    }
  };

  /*------------------------- Requetes API Sector ------------------------*/
  const refreshAllowedSectors = async ( sitesId: string [] ) => {
    if ( sitesId.length > 0 && me ) {
      const sectors = ( await SectorService.getAllowedSectorsBySitesId( sitesId, token, dispatch ) )
        .filter( sector => { return CommonGlobalService.isAdminOfSector( me, sector.id ); } );
      if ( sectors != null ) {
        const optionsSectors = CommonGlobalService.toOptions( sectors );
        const groupedSectors: GroupedOption<Sector>[] = [];
        for ( const [ siteId, options ] of Object.entries( CommonGlobalService.groupBy( optionsSectors, option => { return option.object.siteId; } ) ) ) {
          const optionSectors = options ?? [];
          groupedSectors.push( {
            label: selectedSites.find( site => { return site.object.id === siteId; } )?.object.name ?? t( 'common:error.missing_site' ),
            options: optionSectors
          } );
        }
        setAllowedSectors( groupedSectors );
      }
    } else {
      setAllowedSectors( [] );
    }
  };

  /*----------------------- Requetes API Abilities -----------------------*/
  const refreshAllowedAbilities = async ( sitesId: string[] ) => {
    if ( sitesId.length > 0 ) {
      let abilities = await AbilityService.getAllowedAbilitiesBySitesId( sitesId, token, dispatch );
      if ( abilities != null ) {
        abilities = abilities.map( ability => { return { ...ability, expirationDate: moment().add( 1, 'days' ).startOf( 'day' ).toISOString() }; } );
        const updatedAbilities = abilities.map( item => { return item.id; } ).sort().join( '-' );
        const currentAbilities = allowedAbilities.map( item => { return item.object.id; } ).sort().join( '-' );
        if ( updatedAbilities != currentAbilities ) {
          setAllowedAbilities( CommonGlobalService.toOptions( abilities ) );
        }
      }
    } else {
      setAllowedAbilities( [] );
    }
  };

  /*------------------------- Requetes API Skills ------------------------*/
  const refreshAllowedSkills = async ( sitesId: string[] ) => {
    if ( sitesId.length > 0 ) {
      let skills = await SkillService.getAllowedSkillsBySitesId( sitesId, token, dispatch );
      if ( skills != null ) {
        skills = skills.map( skill => { return { ...skill, value: 1 }; } );
        const updatedSkills = skills.map( item => { return item.id; } ).sort().join( '-' );
        const currentSkills = allowedSkills.map( item => { return item.object.id; } ).sort().join( '-' );
        if ( updatedSkills != currentSkills ) {
          setAllowedSkills( CommonGlobalService.toOptions( skills ) );
        }
      }
    } else {
      setAllowedSkills( [] );
    }
  };


  /*-------------------------------- Front -------------------------------*/
  return (
    <>
      {
        me && (

          <div className='flex flex-col max-w-[550px] xl:max-w-[800px] h-full w-full gap-4 items-center'>

            <div className='w-full'>
              <MultiSelect
                label={ t( 'Client' ) }
                disabled={ !user.isAdminOfUser || allowedClients.find( client => { return !client.isDisabled; } ) == null }
                placeholder={ t( 'No associated clients' ) }
                getValues={ allowedClients }
                selectedValues={ selectedClients }
                clearable={ false }
                onChange={ setSelectedClients }
                extra={(
                  selectedValue => {
                    return (
                      <SwitchAdminUser
                        objectId={ selectedValue.value }
                        isDisabled={ !CommonGlobalService.isAdminOfClient( me, selectedValue.object.id ) || selectedValue.isDisabled }
                        defaultValue={ selectedValue.object.isAdmin }
                        onChange={ value => {
                          selectedValue.object.isAdmin = value;
                          const findedClient = selectedClients.find( item => { return selectedValue.object.id == item.object.id;  } );
                          if ( findedClient ) {
                            findedClient.object.isAdmin = value;
                          }
                          setSelectedClients( structuredClone( selectedClients ) );
                        } }
                      />
                    );
                  }
                )}
                formatOptionLabel={ item => {
                  return (
                    <div
                      className={
                        item.color ?
                          (
                            GlobalService.getTextColor( item.color ) === 'white' ?
                              'text-white-base'
                              :
                              'text-select-item-selected-text'
                          )
                          :
                          undefined
                      }
                    >
                      { item.label }
                    </div>
                  );
                }
                }
              />
            </div>

            <div className='w-full'>
              <MultiSelect
                required={ selectedClients.length > 0 }
                label={ t( 'Site' ) }
                disabled={(
                  !user.isAdminOfUser ||
                  GlobalService.getAllOptions( allowedSites )
                    .every( site => {
                      return selectedSites.find( selectedSite => { return selectedSite.value === site.value; } )?.isDisabled === true;
                    } )
                )}
                placeholder={ t( 'No associated sites' ) }
                getValues={ allowedSites }
                selectedValues={ selectedSites }
                clearable={ false }
                onChange={ setSelectedSites }
                extra={(
                  selectedValue => {
                    return (
                      <SwitchAdminUser
                        objectId={ selectedValue.value }
                        isDisabled={ !CommonGlobalService.isAdminOfSite( me, selectedValue.object.id ) || selectedValue.isDisabled }
                        defaultValue={ selectedValue.object.isAdmin }
                        onChange={ value => {
                          selectedValue.object.isAdmin = value;
                          const findedSite = selectedSites.find( item => { return selectedValue.object.id == item.object.id;  } );
                          if ( findedSite ) {
                            findedSite.object.isAdmin = value;
                          }
                          setSelectedSites( structuredClone( selectedSites ) );
                        } }
                      />
                    );
                  }
                )}
                formatOptionLabel={ item => {
                  return (
                    <div
                      className={
                        item.color ?
                          (
                            GlobalService.getTextColor( item.color ) === 'white' ?
                              'text-white-base'
                              :
                              'text-select-item-selected-text'
                          )
                          :
                          undefined
                      }
                    >
                      { item.label }
                    </div>
                  );
                } }
              />
            </div>

            <div className='w-full'>
              <MultiSelect
                required={ selectedClients.length > 0 }
                label={ t( 'Sector' ) }
                disabled={(
                  !user.isAdminOfUser ||
                  GlobalService.getAllOptions( allowedSectors )
                    .every( sector => {
                      return selectedSectors.find( selectedSector => { return selectedSector.value === sector.value; } )?.isDisabled === true;
                    } )
                )}
                placeholder={ t( 'No associated sectors' ) }
                getValues={ allowedSectors }
                selectedValues={ selectedSectors }
                clearable={ false }
                onChange={ setSelectedSectors }
                extra={(
                  selectedValue => {
                    return (
                      <SwitchAdminUser
                        objectId={ selectedValue.value }
                        isDisabled={ !CommonGlobalService.isAdminOfSector( me, selectedValue.object.id ) || selectedValue.isDisabled }
                        defaultValue={ selectedValue.object.isAdmin }
                        onChange={ value => {
                          selectedValue.object.isAdmin = value;
                          const findedSector = selectedSectors.find( item => { return selectedValue.object.id == item.object.id;  } );
                          if ( findedSector ) {
                            findedSector.object.isAdmin = value;
                          }
                          setSelectedSectors( structuredClone( selectedSectors ) );
                        } }
                      />
                    );
                  }
                )}
              />
            </div>
            {(
              user.isAdminOfUser ?
                <div className='flex flex-col w-full gap-4'>
                  <div className='w-full'>
                    <MultiSelect
                      label={ t( 'Authorization' ) }
                      disabled={ !( user.isAdminOfUser && ( me && ( GlobalService.isUserAdmin( me ) || me.isSuperAdmin ) ) ) || allowedAbilities.length == 0 }
                      placeholder={ t( 'No associated authorizations' ) }
                      getValues={ allowedAbilities }
                      selectedValues={ selectedAbilities }
                      clearable={ false }
                      onChange={ setSelectedAbilities }
                      extra={(
                        ability => {
                          return (
                            <ExpirationAbility
                              ability={ ability.object as Ability }
                              onChange={ changedAbility => {
                                const findedAbility = selectedAbilities.find( item => { return changedAbility.id == item.object.id;  } );
                                if ( findedAbility ) {
                                  findedAbility.object.expirationDate = changedAbility.expirationDate;
                                }
                                setSelectedAbilities( structuredClone( selectedAbilities ) );
                              } }
                            />
                          );
                        }
                      )}
                    />
                  </div>

                  <div className='w-full'>
                    <MultiSelect
                      label={ t( 'Skill' ) }
                      disabled={ !( user.isAdminOfUser && ( me && ( GlobalService.isUserAdmin( me ) || me.isSuperAdmin ) ) ) || allowedSkills.length == 0 }
                      placeholder={ t( 'No associated skills' ) }
                      getValues={ allowedSkills }
                      selectedValues={ selectedSkills }
                      clearable={ false }
                      onChange={ setSelectedSkills }
                      extra={
                        skill => {
                          return (
                            <ILUOSkill
                              skill={ ( skill.object as Skill ) }
                              onChange={ changedSkill => {
                                const findedSkill = selectedSkills.find( item => { return changedSkill.id == item.object.id;  } );
                                if ( findedSkill ) {
                                  findedSkill.object.value = changedSkill.value;
                                }
                                setSelectedSkills( structuredClone( selectedSkills ) );
                              } }
                            />
                          );
                        }
                      }
                    />
                  </div>
                </div>
                :
                null
            )}

          </div>
        )
      }
    </>
  );
}